import { useEffect, useState } from "react";
import { Card, CardContent } from "@material-ui/core";
import { DateField } from "../components/DateField";
import LoadingData from "components/Loading/LoadingData";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";
import CardRepresentantBillingOrders from "../components/CardRepresentantBillingOrders";

const date = new Date();
const today = date.toISOString().slice(0, 10);
const firstDayOfCurrentMonth = new Date(date.getFullYear(), date.getMonth(), 1)
  .toISOString()
  .slice(0, 10);
const lastDayOfCurrentMonth = new Date(
  date.getFullYear(),
  date.getMonth() + 1,
  0
)
  .toISOString()
  .slice(0, 10);
const firstDayOfCurrentYear = new Date(date.getFullYear(), 0, 1)
  .toISOString()
  .slice(0, 10);

function RepresentantSellIn({
  filters,
  structureLoading,
  setIsLoadingAll,
  handleApiError,
}) {
  const { setLocalStorageActions } = useLogs();
  const userDetails = JSON.parse(localStorage.getItem("userDetail"));
  const userDetailsVision = JSON.parse(
    localStorage.getItem("userDetailVision")
  );
  const userInfo = userDetailsVision ? userDetailsVision : userDetails;

  const ordersPageRedirectUrl = "/consulta-de-pedidos?";
  const hasAccessToOrders = userInfo?.ViewsMenu?.find(
    (item) => item.NomeInterno === "ConsultaDePedidos"
  );

  const titlesPageRedirectUrl = "/admin/relatorio-acompanhamento-titulos?";
  const hasAccessToTitles = userInfo?.ViewsMenu?.find(
    (item) => item.NomeInterno === "RelatorioAcompanhamentoTitulos"
  );

  const [monthBillingOrders, setMonthBillingOrders] = useState([]);
  const [accumulatedBillingOrders, setAccumulatedBillingOrders] = useState({
    charts: [],
    bestSellerProducts: [],
    bestCustomers: [],
  });
  const [period, setPeriod] = useState({
    initialDate: firstDayOfCurrentYear,
    finalDate: today,
  });
  const [searchingByPeriod, setSearchingByPeriod] = useState(false);

  const getBilledOrders = async (initialDate, endDate, forCustomPeriod) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=true" +
          `&idRepresentante=${filters?.idRepresentante}` +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      if (forCustomPeriod) {
        const totalOrders = {
          title: "Pedidos implantados",
          total: response.Result.QuantidadePedidos,
          noChart: true,
        };

        const billedOrders = {
          title: "Pedidos faturados",
          total: response.Result.QuantidadeTotalFaturada,
          noChart: true,
        };

        return { totalOrders, billedOrders };
      }
      const queryString = `idGrupoEconomico=${filters?.idGrupoEconomico}&status=FATURADO`;
      const ordersUrl = ordersPageRedirectUrl + queryString;

      const billed = {
        title: "Total Faturado",
        total: `R$ ${response.Result.TotalFaturadoGeral}`,
        noChart: true,
        openUrl:
          hasAccessToOrders && filters?.idGrupoEconomico ? ordersUrl : null,
      };

      const billedOrdersCategories = response.Result.Pedidos.filter((item) =>
        ["Faturados", "Parcialmente Faturado"].includes(item.StatusPedidos)
      ).map((item) => ({
        title:
          item.StatusPedidos === "Parcialmente Faturado"
            ? "Fat. Parcial"
            : "Fat. Total",
        total: Number(item.Quantidade),
        datalabel: item.Quantidade,
      }));

      const billedOrders = {
        title: "Pedidos faturados",
        total: response.Result.QuantidadeTotalFaturada,
        categories: billedOrdersCategories,
        noChart: +response.Result.QuantidadeTotalFaturada === 0,
      };

      return { billedOrders, billed };
    } catch (error) {
      handleApiError(error, "order-sell-in-statusdashSellin=porStatus");
      return {};
    }
  };

  const getBillingInfo = async (initialDate, endDate) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=porCategoria" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters?.idGrupoEconomico}`
            : "") +
          `&idRepresentante=${filters?.idRepresentante}` +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const billedCategories =
        response.Result.PedidosFaturados.PedidosFaturados.map((item) => ({
          title: item.Categoria,
          total: Number(
            item.TotalFaturadoCategoria.replaceAll(".", "").replace(",", ".")
          ),
          datalabel: `R$ ${item.TotalFaturadoCategoria}`,
        }));

      const billed = {
        title: "Total Faturado",
        total: `R$ ${response.Result.PedidosFaturados.TotalFaturado}`,
        categories: billedCategories,
      };

      const periodQueryString = `dtInit=${initialDate}&dtFim=${endDate}`;
      const expiredTitlesQueryString = "&expired=true";

      const expiredTitlesUrl =
        titlesPageRedirectUrl + periodQueryString + expiredTitlesQueryString;

      const pendingBillingCategories =
        response.Result.PedidosFaturados.PedidosPendenteFaturamento.map(
          (item) => ({
            title: item.Categoria,
            total: Number(
              item.TotalPendenteFaturadoCategoria.replaceAll(".", "").replace(
                ",",
                "."
              )
            ),
            datalabel: `R$ ${item.TotalPendenteFaturadoCategoria}`,
          })
        );

      const pendingBilling = {
        title: "Pendente de faturamento",
        total: `R$ ${response.Result.PedidosFaturados.TotalPendeteFaturamento}`,
        categories: pendingBillingCategories,
        openUrl: hasAccessToTitles ? expiredTitlesUrl : null,
      };

      return { pendingBilling, billed };
    } catch (error) {
      handleApiError(error, "order-sell-in-statusdashSellin=porCategoria");
      return {};
    }
  };

  const getOrdersInfo = async (initialDate, endDate, forCustomPeriod) => {
    try {
      const response = await Api.get(
        "order-sell-in-summary",
        "reportApp",
        "?dashSellin=porStatus" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters?.idGrupoEconomico}`
            : "") +
          `&idRepresentante=${filters?.idRepresentante}` +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const shoppingCategories = response?.Result?.Categorias?.map((item) => ({
        title: item.Categoria,
        total: Number(item.ValorTotal.replaceAll(".", "").replace(",", ".")),
        datalabel: `R$ ${item.ValorTotal}`,
      }));

      const shoppingInfo = {
        title: "Total vendido",
        total: `R$ ${response?.Result?.TotalVendas ?? "0,00"}`,
        categories: shoppingCategories ?? [],
        noChart: !shoppingCategories,
      };

      const queryString = `idGrupoEconomico=${filters?.idGrupoEconomico}`;
      const ordersUrl = ordersPageRedirectUrl + queryString;

      const ordersCategories = response?.Result?.Categorias?.map((item) => ({
        title: item.Categoria,
        total: item.Quantidade,
        datalabel: `${item.Quantidade}`,
      }));

      const ordersInfo = {
        title: "Total de pedidos",
        total: response?.Result?.TotalPedidos ?? 0,
        categories: ordersCategories ?? [],
        noChart: !ordersCategories,
        openUrl:
          filters?.idGrupoEconomico && hasAccessToOrders ? ordersUrl : null,
      };

      if (forCustomPeriod) return { shoppingInfo };

      return { ordersInfo, shoppingInfo };
    } catch (error) {
      handleApiError(error, "order-sell-in-summary&dashSellin=porStatus");
      return {};
    }
  };

  const getCustomersInfo = async (initialDate, endDate, forCustomPeriod) => {
    try {
      const response = await Api.get(
        "order-sell-in-summary",
        "reportApp",
        "?dashSellin=porClientes" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters?.idGrupoEconomico}`
            : "") +
          `&idRepresentante=${filters?.idRepresentante}` +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const periodQueryString = `dtInit=${initialDate}&dtFim=${endDate}`;
      const expiredTitlesQueryString = "&expired=true";

      const expiredTitlesUrl =
        titlesPageRedirectUrl + periodQueryString + expiredTitlesQueryString;

      const newCustomers = {
        title: "Novos clientes",
        total: response.Result.DadosClienteTiket?.NovosCliente || "-",
        noChart: true,
      };

      const visits = {
        title: "Número de visitas",
        total: response.Result.DadosClienteTiket?.NumeroVisitas || "-",
        noChart: true,
      };

      const expiredTitles = {
        title: "Títulos vencidos",
        total: response.Result.TotalTitulosVencidos,
        noChart: true,
        openUrl: hasAccessToTitles ? expiredTitlesUrl : null,
      };

      const overdueTitles = {
        title: "Total em atraso",
        total: response.Result.TotalTitulosVencidosFormat,
        noChart: true,
      };

      if (forCustomPeriod) return { expiredTitles, overdueTitles };

      return { newCustomers, visits, expiredTitles };
    } catch (error) {
      handleApiError(error, "order-sell-in-summary&dashSellin=porClientes");
      return {};
    }
  };

  const getBestCustomers = async (initialDate, endDate) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=porClienteRanking" +
          `&idRepresentante=${filters?.idRepresentante}` +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      if (!response?.Result) return [];

      const bestCustomers =
        response?.Result?.ClientesRanking.ListClienteRanking?.map((item) => ({
          title: item.NomeCliente,
          total: item.QuantidadeTotal,
        }));

      return bestCustomers;
    } catch (error) {
      handleApiError(
        error,
        "order-sell-in-summary&dashSellin=porClienteRanking"
      );
      return [];
    }
  };

  const getBestSellerProducts = async (initialDate, endDate) => {
    try {
      const response = await Api.get(
        "order-sell-in-status",
        "reportApp",
        "?dashSellin=porProduto" +
          (filters?.idLoja ? `&idLoja=${filters?.idLoja}` : "") +
          (filters?.idGrupoEconomico
            ? `&idGrupoEconomico=${filters?.idGrupoEconomico}`
            : "") +
          "&dtIni=" +
          initialDate +
          "&dtFim=" +
          endDate,
        true
      );

      const bestSellerProducts =
        response.Result.ProdutosRanking.ListProdutosRanking.map((item) => ({
          title: item.NomeProduto,
          total: item.Quantidade,
        }));

      return bestSellerProducts;
    } catch (error) {
      handleApiError(error, "order-sell-in-status&dashSellin=porProduto");
      return [];
    }
  };

  const fetchData = async () => {
    try {
      if (structureLoading.length > 0 || !filters?.idRepresentante) {
        return;
      }

      setIsLoadingAll(true);

      const [monthOrders, monthBilledOrders, monthClients] = await Promise.all([
        getOrdersInfo(firstDayOfCurrentMonth, lastDayOfCurrentMonth),
        getBilledOrders(firstDayOfCurrentMonth, lastDayOfCurrentMonth),
        getCustomersInfo(firstDayOfCurrentMonth, lastDayOfCurrentMonth),
      ]);

      const [
        accumulatedOrders,
        accumulatedBilledOrders,
        accumulatedBestSellers,
        accumulatedBestCustomers,
        accumulatedBilling,
        accumulateClients,
      ] = await Promise.all([
        getOrdersInfo(firstDayOfCurrentYear, today, true),
        getBilledOrders(firstDayOfCurrentYear, today, true),
        getBestSellerProducts(firstDayOfCurrentYear, today),
        getBestCustomers(firstDayOfCurrentYear, today),
        getBillingInfo(firstDayOfCurrentYear, today),
        getCustomersInfo(firstDayOfCurrentMonth, lastDayOfCurrentMonth, true),
      ]);

      const cleanedMonthBillingOrders = [
        ...Object.values(monthOrders || {}),
        ...Object.values(monthBilledOrders || {}),
        ...Object.values(monthClients || {}),
      ].filter(Boolean);

      setMonthBillingOrders(cleanedMonthBillingOrders);

      const cleanedAccumulatedBillingOrders = [
        ...Object.values(accumulatedOrders || {}),
        ...Object.values(accumulatedBilling || {}),
        ...Object.values(accumulatedBilledOrders || {}),
        ...Object.values(accumulateClients || {}),
      ].filter(Boolean);

      setAccumulatedBillingOrders({
        charts: cleanedAccumulatedBillingOrders,
        bestSellerProducts: accumulatedBestSellers || [],
        bestCustomers: accumulatedBestCustomers || [],
      });

      setIsLoadingAll(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoadingAll(false);
    }
  };

  const handleSearchByPeriod = async (initialDate, finalDate) => {
    setSearchingByPeriod(true);

    try {
      const [
        accumulatedOrders,
        accumulatedBilledOrders,
        accumulatedBestSellers,
        accumulatedBestCustomers,
        accumulatedBilling,
        accumulateClients,
      ] = await Promise.all([
        getOrdersInfo(initialDate, finalDate, true),
        getBilledOrders(initialDate, finalDate, true),
        getBestSellerProducts(initialDate, finalDate),
        getBestCustomers(initialDate, finalDate),
        getBillingInfo(initialDate, finalDate),
        getCustomersInfo(firstDayOfCurrentMonth, lastDayOfCurrentMonth, true),
      ]);

      const cleanedAccumulatedBillingOrders = [
        ...Object.values(accumulatedOrders || {}),
        ...Object.values(accumulatedBilling || {}),
        ...Object.values(accumulatedBilledOrders || {}),
        ...Object.values(accumulateClients || {}),
      ].filter(Boolean);

      setAccumulatedBillingOrders({
        charts: cleanedAccumulatedBillingOrders,
        bestSellerProducts: accumulatedBestSellers || [],
        bestCustomers: accumulatedBestCustomers || [],
      });

      setPeriod({ initialDate, finalDate });

      setLocalStorageActions({
        nomeTela: "Dashboard SellIn",
        idMenu: "95",
        acao: "Clicou para Pesquisar por período",
      });
    } catch (error) {
      console.error("Error searching by period:", error);
    } finally {
      setSearchingByPeriod(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    filters?.idRepresentante,
    filters?.idLoja,
    filters?.idGrupoEconomico,
    structureLoading,
  ]);

  if (monthBillingOrders.length === 0) {
    return null;
  }

  return (
    <>
      <CardRepresentantBillingOrders
        isAgent={true}
        title="Faturamento e pedidos/mês"
        data={{ charts: monthBillingOrders }}
        type="month"
      />

      <Card>
        <CardContent>
          <DateField
            label={"Período"}
            isLoading={searchingByPeriod}
            onSubmit={handleSearchByPeriod}
          />
        </CardContent>
      </Card>

      <div style={{ width: "100%" }}>
        {!searchingByPeriod && (
          <CardRepresentantBillingOrders
            title={"Faturamento e pedidos acumulados"}
            period={period}
            data={{ ...accumulatedBillingOrders }}
            type="year"
          />
        )}
        <LoadingData searching={searchingByPeriod} label="Buscando dados..." />
      </div>
    </>
  );
}

export default RepresentantSellIn;
